<template>
  <div class="info-request fbc-table-header-labels" data-testid="info-request">
    <div
      v-if="requestsLoading"
      class="info-request__loading d-flex justify-center py-4"
      data-testid="info-request__loading"
    >
      <v-progress-circular indeterminate color="primary" size="32" />
    </div>
    <lb-datatableaction
      v-else-if="requestRows.length"
      :headers="headers"
      :tabledata="requestRows"
      :displayed-row-count="requestRows.length"
      :itemsperpage="requestRows.length || 25"
      :bulk-actions="false"
      :show-toolbar="false"
      :auto-height="true"
      :enable-column-reorder="false"
      header-bg-class="fbc-table-header-bg"
      data-testid="info-request__table"
    >
      <template #tbody>
        <tr
          v-for="(row, index) in requestRows"
          :key="row._id || index"
          class="info-request__row"
          :data-testid="`info-request__row-${index}`"
        >
          <td class="px-4 single-line" :data-testid="`info-request__cell-name-${index}`">
            {{ row.name }}
          </td>
          <td class="px-4 info-request__cell-description" :data-testid="`info-request__cell-description-${index}`">
            {{ row.description }}
          </td>
          <td class="px-4 single-line" :data-testid="`info-request__cell-type-${index}`">
            {{ row.type }}
          </td>
          <td class="px-4 single-line" :data-testid="`info-request__cell-category-${index}`">
            {{ row.category }}
          </td>
          <td class="px-4 single-line" :data-testid="`info-request__cell-requested-from-${index}`">
            {{ row.requestedFrom }}
          </td>
          <td class="px-4" :data-testid="`info-request__cell-status-${index}`">
            <StatusIndicator
              :variant="row.statusVariant"
              :custom-label="row.statusLabel"
              size="sm"
              outlined
            />
          </td>
          <td class="info-request__cell-actions" :data-testid="`info-request__cell-actions-${index}`">
            <div class="info-request__actions" :data-testid="`info-request__actions-wrapper-${index}`">
              <v-tooltip bottom content-class="tooltip-bottom" open-delay="500">
                <template v-slot:activator="{ on, attrs }">
                  <button
                    type="button"
                    class="info-request__action-btn"
                    v-bind="attrs"
                    v-on="on"
                    :disabled="row.raw.stage !== 1"
                    :data-testid="`info-request__approve-btn-${index}`"
                    @click="$emit('approve', row.raw)"
                  >
                    <img
                      src="/img/icons/fbc-icons/ApproveTik.svg"
                      alt=""
                      class="info-request__action-icon"
                      width="16"
                      height="16"
                    />
                  </button>
                </template>
                <span>Approve</span>
              </v-tooltip>

              <v-tooltip bottom content-class="tooltip-bottom" open-delay="500">
                <template v-slot:activator="{ on, attrs }">
                  <button
                    type="button"
                    class="info-request__action-btn"
                    v-bind="attrs"
                    v-on="on"
                    :disabled="row.raw.stage !== 1"
                    :data-testid="`info-request__reject-btn-${index}`"
                    @click="$emit('reject', row.raw)"
                  >
                    <img
                      src="/img/icons/fbc-icons/RejectClose.svg"
                      alt=""
                      class="info-request__action-icon"
                      width="16"
                      height="16"
                    />
                  </button>
                </template>
                <span>Reject</span>
              </v-tooltip>

              <v-tooltip
                v-if="row.canDownload"
                bottom
                content-class="tooltip-bottom"
                open-delay="500"
              >
                <template v-slot:activator="{ on, attrs }">
                  <button
                    type="button"
                    class="info-request__action-btn"
                    v-bind="attrs"
                    v-on="on"
                    :data-testid="`info-request__download-btn-${index}`"
                    @click="handleDownload(row.raw)"
                  >
                    <img
                      src="/img/icons/DownloadIcon.svg"
                      alt=""
                      class="info-request__action-icon"
                    />
                  </button>
                </template>
                <span>Download</span>
              </v-tooltip>
            </div>
          </td>
        </tr>
      </template>
    </lb-datatableaction>

    <FilesRequestsEmptyState
      v-else
      variant="requests"
      data-testid="info-request__empty"
    />
  </div>
</template>

<script>
import './InfoRequest.scss';
import StatusIndicator from '@/components/StatusIndicator.vue';
import FilesRequestsEmptyState from './FilesRequestsEmptyState.vue';
import { STATUS_VARIANTS } from '@/components/utils/status-constants';
import {
  canDownloadAttachments,
  downloadAllAttachments,
} from '@/modules/financialclose/views/Project/InformationRequests/utils/requestDetail';

const TABLE_HEADERS = [
  { text: 'Name', value: 'name', visible: true, sortable: false, filterable: false },
  { text: 'Requested Information', value: 'description', visible: true, sortable: false, filterable: false },
  { text: 'Type', value: 'type', visible: true, sortable: false, filterable: false },
  { text: 'Classification', value: 'category', visible: true, sortable: false, filterable: false },
  { text: 'Requested From', value: 'requestedFrom', visible: true, sortable: false, filterable: false },
  { text: 'Status', value: 'status', visible: true, sortable: false, filterable: false },
  { text: 'Action', value: 'action', visible: true, sortable: false, filterable: false },
];

const STAGE_STATUS = {
  0: { variant: STATUS_VARIANTS.PENDING_SUBMISSION, label: 'Pending Submission' },
  1: { variant: STATUS_VARIANTS.FC_PENDING_APPROVAL, label: 'Pending Approval' },
  2: { variant: STATUS_VARIANTS.FC_APPROVED, label: 'Closed' },
  '-1': { variant: STATUS_VARIANTS.FC_REJECTED, label: 'Cancelled' },
};

export default {
  name: 'InfoRequest',

  components: {
    StatusIndicator,
    FilesRequestsEmptyState,
  },

  props: {
    inforeqstage: { type: Number, default: 0 },
    metadata: { type: Object, default: () => ({}) },
    task: { type: Object, required: true },
  },

  data() {
    return {
      loadedRequests: [],
      requestsLoading: false,
      requestsRequestId: 0,
    };
  },

  watch: {
    task: {
      immediate: true,
      handler() {
        this.loadDetailRequests();
      },
    },
  },

  beforeDestroy() {
    this.requestsRequestId = 0;
  },

  computed: {
    headers() {
      return TABLE_HEADERS;
    },

    filteredInfoRequests() {
      return this.loadedRequests;
    },

    requestRows() {
      return this.filteredInfoRequests.map((request) => {
        const stageInfo = STAGE_STATUS[request.stage] || STAGE_STATUS[0];
        return {
          _id: request._id,
          name: request.info_owner_detail?.name || '—',
          description: request.description || '—',
          type: request.type || '—',
          category: request.category || '—',
          requestedFrom: request.info_owner_detail?.email || '—',
          statusVariant: stageInfo.variant,
          statusLabel: stageInfo.label,
          canDownload: canDownloadAttachments(request),
          raw: request,
        };
      });
    },
  },

  methods: {
    handleDownload(request) {
      downloadAllAttachments(this.$nova, request);
    },

    /**
     * Full request objects come from getTask. A list row only has counts.
     * If this task payload already carries the array (getTask or a row patched
     * from getTask), use that and skip a second read.
     */
    async loadDetailRequests() {
      const requestToken = this.requestsRequestId + 1;
      this.requestsRequestId = requestToken;
      const stillCurrent = () => this.requestsRequestId === requestToken;
      const taskId = this.task?._id || '';
      const embedded = this.task?.__computed?.infoManagerRequests;
      if (!stillCurrent()) return;
      if (!taskId) {
        this.loadedRequests = [];
        this.requestsLoading = false;
        return;
      }
      if (Array.isArray(embedded)) {
        this.loadedRequests = embedded;
        this.requestsLoading = false;
        return;
      }
      const checklistId = this.task.project
        || this.metadata?._id
        || this.$route?.params?.projectId
        || this.$route?.params?.id
        || '';
      if (!checklistId) {
        this.loadedRequests = [];
        this.requestsLoading = false;
        return;
      }
      this.requestsLoading = true;
      this.loadedRequests = [];
      try {
        const requests = await this.$store.dispatch('tasks/fetchTaskInfoRequests', {
          checklistId,
          taskId,
        });
        if (!stillCurrent()) return;
        this.loadedRequests = Array.isArray(requests) ? requests : [];
      } catch (err) {
        if (!stillCurrent()) return;
        console.error('[InfoRequest]: fetchTaskInfoRequests', err);
        this.loadedRequests = [];
      } finally {
        if (stillCurrent()) this.requestsLoading = false;
      }
    },
  },
};
</script>

